import { Plus, Trash2 } from 'lucide-react';
import {
  type FooterColumn,
  minimumFooterBand,
  type PrintFooter,
  type PrintTemplateDocument,
} from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { ColorField } from '../controls/ColorField';
import { LinesField } from '../controls/LinesField';
import { NumberField } from '../controls/NumberField';
import { ALIGNMENT_OPTIONS } from './alignment-options';
import { FOOTER_COLUMN_MAX_LINES, FOOTER_MAX_COLUMNS } from '#shared/types';

interface FooterSectionProps {
  document: PrintTemplateDocument;
  onChange: (patch: Partial<PrintFooter>) => void;
}

interface FooterFieldsProps {
  footer: PrintFooter;
  onChange: FooterSectionProps['onChange'];
}

export function FooterSection({ document, onChange }: FooterSectionProps) {
  const footer = document.footer;

  return (
    <>
      <Checkbox
        label="Print a footer"
        checked={footer.enabled}
        onChange={(event) => onChange({ enabled: event.target.checked })}
      />

      {!footer.enabled && (
        <NumberField
          label="Blank space at the bottom"
          unit="px"
          value={footer.blank_space}
          onChange={(blank_space) => onChange({ blank_space })}
          min={0}
          max={800}
          step={1}
          helpText="For paper with a footer already printed on it: the report and its signatories end above this much clear space."
        />
      )}

      {footer.enabled && (
        <>
          <BandFields document={document} onChange={onChange} />

          <FooterColumns footer={footer} onChange={onChange} />

          <PageNumberFields footer={footer} onChange={onChange} />

          <StripFields footer={footer} onChange={onChange} />
        </>
      )}
    </>
  );
}

/**
 * The contact blocks — the lab's phone numbers, address and any other fixed
 * text. The people who sign are the Signatures section, read from the reports.
 */
function FooterColumns({
  footer,
  onChange,
}: {
  footer: PrintFooter;
  onChange: FooterSectionProps['onChange'];
}) {
  function replaceAt(index: number, column: FooterColumn) {
    onChange({
      columns: footer.columns.map((current, position) => (position === index ? column : current)),
    });
  }

  return (
    <div className="c-builder__blocks">
      <p className="c-builder__blocks-label">Contact and other text</p>

      {footer.columns.map((column, index) => (
        // Positional slots with no stable identity of their own — two blank
        // blocks are genuinely the same value.
        <div className="c-builder__block" key={index}>
          <div className="c-builder__block-head">
            <TextField
              label={`Block ${index + 1} heading`}
              hideLabel
              value={column.heading}
              placeholder="Name or heading"
              onChange={(event) => replaceAt(index, { ...column, heading: event.target.value })}
            />
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 />}
              aria-label={`Remove block ${index + 1}`}
              onClick={() =>
                onChange({ columns: footer.columns.filter((_, position) => position !== index) })
              }
            />
          </div>

          <LinesField
            label="Lines"
            lines={column.lines}
            max={FOOTER_COLUMN_MAX_LINES}
            onChange={(lines) => replaceAt(index, { ...column, lines })}
            placeholder="Phone number, address…"
          />

          <Select
            label="Alignment"
            value={column.align}
            onChange={(choice) => replaceAt(index, { ...column, align: choice })}
            options={ALIGNMENT_OPTIONS}
          />
        </div>
      ))}

      {footer.columns.length < FOOTER_MAX_COLUMNS && (
        <Button
          size="sm"
          icon={<Plus />}
          onClick={() =>
            onChange({
              columns: [...footer.columns, { heading: '', lines: [], align: 'left' }],
            })
          }
        >
          Add block
        </Button>
      )}
    </div>
  );
}

/** When the footer prints, how deep its band is, and the rule above it. */
function BandFields({ document, onChange }: FooterSectionProps) {
  const footer = document.footer;
  const minimum = Math.ceil(minimumFooterBand(document) * 10) / 10;
  const short = footer.band < minimum;

  return (
    <>
      <Select
        label="Print it on"
        value={footer.repeat}
        onChange={(choice) => onChange({ repeat: choice === 'last' ? 'last' : 'every' })}
        options={[
          { value: 'every', label: 'Every page' },
          { value: 'last', label: 'The last page only' },
        ]}
        helpText="Page numbers print on every page either way."
      />

      <NumberField
        label="Band height"
        unit="mm"
        value={footer.band}
        onChange={(band) => onChange({ band })}
        min={0}
        max={120}
        helpText={
          short
            ? `Your footer needs ${minimum} mm, so ${minimum} mm is what will be used.`
            : `Your footer needs at least ${minimum} mm.`
        }
      />

      <Checkbox
        label="Rule above the footer"
        checked={footer.rule}
        onChange={(event) => onChange({ rule: event.target.checked })}
      />
      {footer.rule && (
        <NumberField
          label="Second-accent segment"
          unit="mm"
          value={footer.rule_accent_width}
          onChange={(rule_accent_width) => onChange({ rule_accent_width })}
          min={0}
          max={200}
        />
      )}
    </>
  );
}

/** Page numbers: whether, in what words, and where. */
function PageNumberFields({ footer, onChange }: FooterFieldsProps) {
  return (
    <>
      <Checkbox
        label="Page numbers"
        checked={footer.page_number.enabled}
        onChange={(event) =>
          onChange({ page_number: { ...footer.page_number, enabled: event.target.checked } })
        }
      />
      {footer.page_number.enabled && (
        <>
          <TextField
            label="Page number wording"
            value={footer.page_number.format}
            onChange={(event) =>
              onChange({ page_number: { ...footer.page_number, format: event.target.value } })
            }
            helpText="{page} and {pages} are replaced — e.g. “Page 2 of 5”."
          />
          <Segmented
            label="Page number alignment"
            value={footer.page_number.align}
            onChange={(align) => onChange({ page_number: { ...footer.page_number, align } })}
            options={ALIGNMENT_OPTIONS}
          />
        </>
      )}
    </>
  );
}

/** The coloured strip along the bottom edge. */
function StripFields({ footer, onChange }: FooterFieldsProps) {
  return (
    <>
      <Checkbox
        label="Bottom strip"
        checked={footer.strip.enabled}
        onChange={(event) =>
          onChange({ strip: { ...footer.strip, enabled: event.target.checked } })
        }
      />
      {footer.strip.enabled && (
        <>
          <TextField
            label="Strip text"
            value={footer.strip.text}
            onChange={(event) => onChange({ strip: { ...footer.strip, text: event.target.value } })}
          />
          <TextField
            label="Strip note"
            value={footer.strip.note}
            onChange={(event) => onChange({ strip: { ...footer.strip, note: event.target.value } })}
            helpText="Set to the right of the strip text."
          />
          <div className="c-builder__grid">
            <ColorField
              label="Strip background"
              value={footer.strip.background}
              onChange={(background) => onChange({ strip: { ...footer.strip, background } })}
            />
            <ColorField
              label="Strip text colour"
              value={footer.strip.foreground}
              onChange={(foreground) => onChange({ strip: { ...footer.strip, foreground } })}
            />
          </div>
        </>
      )}
    </>
  );
}
